import { useState, useCallback, useEffect, useRef, useContext } from 'react';

import { AI_MODEL, FormDoc, FormSummary, QuestionSummary } from '@vyce/core/src/types';
import { createResponseAiRequest } from '@vyce/core/src/api/ai';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

import { useFormAnswers } from './useFormAnswers';

type FormQuestionType = FormDoc['questions'][number];

type SanitizedFormQuestion = Pick<FormQuestionType, 'id' | 'order' | 'title' | 'description'> & {
  type: FormQuestionType['answer']['type'];
  options?: string[];
};

type SanitizedFormDoc = Pick<FormDoc, 'id' | 'title' | 'description' | 'questionCount'> & {
  questions: SanitizedFormQuestion[];
};

type SanitizedQuestionSummary = Pick<
  QuestionSummary,
  'questionId' | 'questionTitle' | 'questionType' | 'totalAnswers'
> & {
  insight: string;
};

type SanitizedFormSummary = Pick<FormSummary, 'formId' | 'formTitle' | 'totalResponses' | 'generatedAt'> & {
  questionSummaries: SanitizedQuestionSummary[];
};

const MAX_OPTIONS = 8;
// GPT-4o supports 128,000 tokens (512,000 characters) - maximum for OpenAI models
// Using 400,000 characters (~100k tokens) to leave room for prompt template and response
const MAX_PROMPT_CHARACTERS = 400000;
const MAX_TOP_RESULTS = 3;
const MAX_TEXT_SAMPLES = 3;
const MAX_DESCRIPTION_LENGTH = 200; // Limit description length to prevent huge prompts
const MAX_QUESTION_TITLE_LENGTH = 150; // Limit question title length

const truncateText = (text: string | undefined, maxLength: number): string | undefined => {
  if (!text) return undefined;
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const sanitizeFormForAI = (form?: FormDoc | null): SanitizedFormDoc | null => {
  if (!form) {
    return null;
  }

  return {
    id: form.id,
    title: truncateText(form.title, MAX_QUESTION_TITLE_LENGTH) || form.title,
    description: truncateText(form.description, MAX_DESCRIPTION_LENGTH),
    questionCount: form.questionCount,
    questions: form.questions.map(question => ({
      id: question.id,
      order: question.order,
      title: truncateText(question.title, MAX_QUESTION_TITLE_LENGTH) || question.title,
      description: truncateText(question.description, MAX_DESCRIPTION_LENGTH),
      type: question.answer.type,
      options: question.answer.options?.slice(0, MAX_OPTIONS).map(option => option.label),
    })),
  };
};

const buildVisualizationInsight = (questionSummary: QuestionSummary): string => {
  const { visualization } = questionSummary;

  if (!visualization) {
    return 'No visualization data available yet.';
  }

  if (visualization.type === 'text_list') {
    const samples = visualization.data
      .map(item =>
        typeof item.value === 'string'
          ? item.value?.trim()
          : typeof item.value === 'object' && item.value?.value
          ? item.value?.value?.trim()
          : undefined
      )
      .filter(Boolean)
      .slice(0, MAX_TEXT_SAMPLES);

    if (!samples.length) {
      return 'No textual answers submitted yet.';
    }

    const suffix = visualization.data.length > samples.length ? '...' : '';
    return `Sample answers: ${samples.map(sample => `"${sample}"`).join(', ')}${suffix}`;
  }

  if (visualization.type === 'bar_chart') {
    const sorted = [...visualization.data].sort((a, b) => b.count - a.count);
    const highlights = sorted
      .slice(0, MAX_TOP_RESULTS)
      .map(point => `${point.label} (${Math.round(point.percentage)}%)`);

    if (!highlights.length) {
      return 'No numeric answers yet.';
    }

    return `Distribution highlights: ${highlights.join(', ')}. Range ${visualization.minLabel}–${
      visualization.maxLabel
    }.`;
  }

  const sorted = [...visualization.data].sort((a, b) => b.count - a.count);
  const highlights = sorted
    .slice(0, MAX_TOP_RESULTS)
    .map(point => `${point.label} (${Math.round(point.percentage)}%)`);

  if (!highlights.length) {
    return 'No answers yet.';
  }

  return `Top answers: ${highlights.join(', ')}`;
};

const sanitizeFormSummaryForAI = (summary?: FormSummary | null): SanitizedFormSummary | null => {
  if (!summary) {
    return null;
  }

  return {
    formId: summary.formId,
    formTitle: summary.formTitle,
    totalResponses: summary.totalResponses,
    generatedAt: summary.generatedAt,
    questionSummaries: summary.questionSummaries.map(question => ({
      questionId: question.questionId,
      questionTitle: question.questionTitle,
      questionType: question.questionType,
      totalAnswers: question.totalAnswers,
      insight: buildVisualizationInsight(question),
    })),
  };
};

const formatFormDataForAI = (
  form: SanitizedFormDoc | null,
  summary: SanitizedFormSummary | null,
  focusOfAnalysis: string
): string => {
  let text = '=== FORM/SURVEY ANALYSIS DATA ===\n\n';

  if (form) {
    text += `FORM TITLE: ${form.title}\n`;
    if (form.description) {
      text += `DESCRIPTION: ${form.description}\n`;
    }
    text += `TOTAL QUESTIONS: ${form.questionCount}\n\n`;

    text += 'QUESTIONS:\n';
    form.questions.forEach((question, index) => {
      text += `\n${index + 1}. ${question.title}\n`;
      if (question.description) {
        text += `   Description: ${question.description}\n`;
      }
      text += `   Type: ${question.type}\n`;
      if (question.options && question.options.length > 0) {
        text += `   Options: ${question.options.join(', ')}\n`;
      }
    });
  }

  if (summary) {
    text += `\n\n=== RESPONSE SUMMARY ===\n`;
    text += `TOTAL RESPONSES: ${summary.totalResponses}\n`;
    text += `GENERATED AT: ${summary.generatedAt}\n\n`;

    text += 'QUESTION SUMMARIES:\n';
    summary.questionSummaries.forEach((qSummary, index) => {
      text += `\n${index + 1}. ${qSummary.questionTitle}\n`;
      text += `   Type: ${qSummary.questionType}\n`;
      text += `   Total Answers: ${qSummary.totalAnswers}\n`;
      text += `   Insights: ${qSummary.insight}\n`;
    });
  }

  if (focusOfAnalysis) {
    text += `\n\n=== FOCUS OF ANALYSIS ===\n${focusOfAnalysis}\n`;
  }

  return text;
};

const truncateDataToFitLimit = (
  form: SanitizedFormDoc | null,
  summary: SanitizedFormSummary | null,
  focusOfAnalysis: string,
  basePromptLength: number
): string => {
  // Calculate available space for data
  const availableSpace = MAX_PROMPT_CHARACTERS - basePromptLength - focusOfAnalysis.length - 500; // 500 for safety margin

  // First, try with full data
  let formattedData = formatFormDataForAI(form, summary, '');

  if (formattedData.length <= availableSpace) {
    // Data fits, return with focus
    return formatFormDataForAI(form, summary, focusOfAnalysis);
  }

  // Data is too large, need to truncate
  // Strategy: keep form data, truncate question summaries
  let text = '=== FORM/SURVEY ANALYSIS DATA ===\n\n';

  if (form) {
    text += `FORM TITLE: ${form.title}\n`;
    if (form.description) {
      text += `DESCRIPTION: ${form.description}\n`;
    }
    text += `TOTAL QUESTIONS: ${form.questionCount}\n\n`;

    text += 'QUESTIONS:\n';
    form.questions.forEach((question, index) => {
      text += `\n${index + 1}. ${question.title}\n`;
      if (question.description) {
        text += `   Description: ${question.description}\n`;
      }
      text += `   Type: ${question.type}\n`;
      if (question.options && question.options.length > 0) {
        text += `   Options: ${question.options.join(', ')}\n`;
      }
    });
  }

  if (summary) {
    text += `\n\n=== RESPONSE SUMMARY ===\n`;
    text += `TOTAL RESPONSES: ${summary.totalResponses}\n`;
    text += `GENERATED AT: ${summary.generatedAt}\n\n`;

    text += 'QUESTION SUMMARIES:\n';

    // Calculate space left for summaries
    const summaryHeaderLength = text.length + focusOfAnalysis.length + 100; // 100 for safety
    const availableForSummaries = availableSpace - summaryHeaderLength;

    // Add summaries one by one until we run out of space
    let summariesText = '';
    for (let i = 0; i < summary.questionSummaries.length; i++) {
      const qSummary = summary.questionSummaries[i];
      const summaryText = `\n${i + 1}. ${qSummary.questionTitle}\n   Type: ${
        qSummary.questionType
      }\n   Total Answers: ${qSummary.totalAnswers}\n   Insights: ${qSummary.insight}\n`;

      if (summariesText.length + summaryText.length > availableForSummaries) {
        // Add note about truncation
        summariesText += `\n... (showing ${i} of ${summary.questionSummaries.length} question summaries due to size limits)\n`;
        break;
      }

      summariesText += summaryText;
    }

    text += summariesText;
  }

  if (focusOfAnalysis) {
    text += `\n\n=== FOCUS OF ANALYSIS ===\n${focusOfAnalysis}\n`;
  }

  return text;
};

interface UseFormAIAnalyticsProps {
  open: boolean;
  selectedFormForAIAnalytics: FormDoc | null;
  userId: string;
  companyId: string;
}

export const useFormAIAnalytics = ({
  open,
  selectedFormForAIAnalytics,
  userId,
  companyId,
}: UseFormAIAnalyticsProps) => {
  const {
    getFormSummary,
    loading: formSummaryLoading,
    formSummary,
    setFormSummary,
  } = useFormAnswers({ userId, companyId });
  const [loading, setLoading] = useState(false);
  const [aiAnalytics, setAiAnalytics] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [focusOfAnalysis, setFocusOfAnalysis] = useState<string>('');
  const { showNotification } = useContext(NotificationContext);
  const markdownRef = useRef<HTMLDivElement>(null);

  const getAIAnalytics = useCallback(
    async (focusOfAnalysisValue: string) => {
      if (!selectedFormForAIAnalytics?.id) {
        return;
      }

      const trimmedFocus = focusOfAnalysisValue.trim();

      try {
        setLoading(true);
        setProgress(20);

        // Prepare form data for AI
        const compactForm = sanitizeFormForAI(selectedFormForAIAnalytics);
        const compactSummary = sanitizeFormSummaryForAI(formSummary);

        setProgress(40);

        // Base prompt template - use trimmedFocus if available, otherwise use default 5 points
        const basePromptTemplate = trimmedFocus
          ? `Below is the complete form/survey analysis data:

{FORM_DATA}

Your task:
Focus your analysis on the specific focus of analysis provided above. Prioritize addressing that focus even if it means adjusting or omitting the standard analysis points. Provide a comprehensive analysis that directly addresses the focus of analysis while still being thorough and useful.

Make your analysis comprehensive but concise. Use the form title, questions, and all answer summaries provided above.`
          : `Below is the complete form/survey analysis data:

{FORM_DATA}

Your task:
Analyze the form/survey data above and provide:
1. Key insights and themes from the responses
2. Patterns you identify (consensus, disagreement, notable outliers)
3. A succinct, well-structured summary in plain language
4. Concrete, actionable recommendations based on what the team said
5. Add emojis to make the analysis more engaging and easy to understand, but keep official tone and language.

Make your analysis comprehensive but concise. Use the form title, questions, and all answer summaries provided above.`;

        // Get or prepare form data
        let formData: string;
        formData = truncateDataToFitLimit(
          compactForm,
          compactSummary,
          trimmedFocus,
          basePromptTemplate.length
        );

        // Build final prompt
        let promptContent = basePromptTemplate.replace('{FORM_DATA}', formData);

        // Verify total size doesn't exceed limit and truncate if needed
        if (promptContent.length > MAX_PROMPT_CHARACTERS) {
          console.warn(
            `Prompt size (${promptContent.length}) exceeds limit (${MAX_PROMPT_CHARACTERS}), truncating...`
          );
          // Re-truncate with stricter limits
          const truncatedFormData = truncateDataToFitLimit(
            compactForm,
            compactSummary,
            trimmedFocus,
            basePromptTemplate.length
          );
          promptContent = basePromptTemplate.replace('{FORM_DATA}', truncatedFormData);

          if (promptContent.length > MAX_PROMPT_CHARACTERS) {
            throw new Error('Data is too large to process. Please reduce the amount of form data.');
          }
        }

        setProgress(60);

        const res = await createResponseAiRequest({
          input: [{ role: 'assistant', content: promptContent }],
          model: AI_MODEL.GPT_4O, // Using GPT-4o with 128k token limit (maximum for OpenAI models)
          tools: [],
        });

        setProgress(90);
        const resContent = res.data.body?.output_text || '';
        setAiAnalytics(resContent);
        setProgress(100);
      } catch (error) {
        console.error(error);
        setProgress(0);
      } finally {
        setLoading(false);
      }
    },
    [formSummary, selectedFormForAIAnalytics]
  );

  const regenerateAIAnalytics = () => {
    setAiAnalytics('');
    setProgress(0);
    getAIAnalytics(focusOfAnalysis);
  };

  const copyToClipboard = async () => {
    if (!aiAnalytics || !markdownRef.current) {
      return;
    }
    const htmlContent = markdownRef.current.innerHTML;
    const textContent = markdownRef.current.innerText || markdownRef.current.textContent || '';

    // Copy both HTML (for formatted paste) and plain text (for fallback)
    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([htmlContent], { type: 'text/html' }),
      'text/plain': new Blob([textContent], { type: 'text/plain' }),
    });

    try {
      await navigator.clipboard.write([clipboardItem]);
    } catch (err) {
      // Fallback to plain text if ClipboardItem is not supported
      await navigator.clipboard.writeText(textContent);
    }

    showNotification({
      message: 'AI analysis copied to clipboard',
      options: {
        variant: 'success',
      },
    });
  };

  const handleClose = () => {
    setAiAnalytics('');
    setProgress(0);
    setFocusOfAnalysis('');
    setFormSummary(null);
  };

  useEffect(() => {
    if (!open || !selectedFormForAIAnalytics?.id) {
      return;
    }

    const formId = selectedFormForAIAnalytics.id;
    if (!formSummary || formSummary.formId !== formId) {
      getFormSummary(formId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedFormForAIAnalytics?.id, formSummary]);

  useEffect(() => {
    if (!open || !formSummary || !selectedFormForAIAnalytics) {
      return;
    }

    setProgress(50);
    getAIAnalytics('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formSummary, selectedFormForAIAnalytics]);

  return {
    loading,
    aiAnalytics,
    progress,
    setProgress,
    focusOfAnalysis,
    setFocusOfAnalysis,
    formSummaryLoading,
    markdownRef,
    regenerateAIAnalytics,
    copyToClipboard,
    handleClose,
  };
};
